import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { resolvePhotoUrl } from '../../utils/photoUrl';
import ZoomableImage from '../../components/ZoomableImage';
import ObservationPanel from '../../components/microscopy/ObservationPanel';
import ScaleBarForm from '../../components/microscopy/ScaleBarForm';
import ScaleBarMark from '../../components/microscopy/ScaleBarMark';
import { errorText } from '../../utils/errorText';
import { toImagePx } from '../../utils/microGeometry';
import { badge, box, input, label } from '../shared/styles';

/*
 * A microscopy image with what it says about itself: the scale bar is drawn
 * from the image's own um/px -- and only when there is one; where it came from
 * (a calibrated instrument, ImageJ, EXIF, a person) is always shown next to
 * it. Links say what the image shows, and are added or removed (with a reason)
 * here. `readOnly` (the hub's culture card) keeps the picture, its scale bar,
 * its metadata and its links, and offers no change: the card types an image
 * from its own select, and the station links and scales.
 *
 * A person sets a scale by measuring the bar burned into the picture: with
 * 📐 drawing on (ScaleBarForm), one pointer draws along the bar over the slide,
 * in the slide's own pixels at any magnification. What a person saw on the
 * slide is recorded below it (ObservationPanel).
 */
export function barPixels(image) {
  if (!image.um_per_px || !image.scale_bar_um) return null;
  return image.scale_bar_um / image.um_per_px;
}

const LINK_KINDS = ['experiment', 'experiment_unit', 'sample', 'external', 'plate', 'slant', 'strain', 'protocol'];

export default function MicroscopyViewer({ image: initial, onChange, readOnly = false }) {
  const { t } = useTranslation('scan');
  const [image, setImage] = useState(initial);
  const [overlay, setOverlay] = useState(true);
  const [reason, setReason] = useState('');
  const [scale, setScale] = useState('');
  const [linkCode, setLinkCode] = useState('');
  const [error, setError] = useState('');
  const [measuring, setMeasuring] = useState(false);
  const [line, setLine] = useState(null);
  const [drawn, setDrawn] = useState(null);
  const svg = useRef(null);
  const W = image.width_px || 100;
  const H = image.height_px || 100;
  const at = e => toImagePx(e.clientX, e.clientY, svg.current.getBoundingClientRect(), W, H);
  const barDown = (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (drawn) { setDrawn(null); return; } // A second finger: that's a pinch, not a line.
    try { svg.current.setPointerCapture?.(e.pointerId); } catch { /* a synthetic event has no pointer */ }
    const p = at(e);
    setDrawn([p, p]);
  };
  const barMove = (e) => { if (drawn) setDrawn([drawn[0], at(e)]); };
  const barUp = () => {
    if (!drawn) return;
    if (drawn[0][0] !== drawn[1][0] || drawn[0][1] !== drawn[1][1]) setLine(drawn);
    setDrawn(null);
  };

  const act = async (fn) => {
    setError('');
    try {
      const { data } = await fn();
      setImage(data);
      setReason('');
      onChange?.(data);
    } catch (e) {
      setError(errorText(e));
    }
  };
  const linkByCode = () => act(async () => {
    const { data: found } = await api.get(`/scan/${encodeURIComponent(linkCode.trim())}`);
    const kind = { collection: 'sample' }[found.kind] || found.kind;
    if (!LINK_KINDS.includes(kind)) throw new Error(t('cannotLink', { kind: found.kind }));
    const result = await api.post(`/microscopy/${image.image_id}/links`, { kind, id: found.id });
    setLinkCode('');
    return result;
  });

  const meta = [
    ['structure', image.structure ? t(`structure.${image.structure}`) : null],
    ['magnification', image.magnification ? `${image.magnification}×` : null],
    ['objective', image.objective], ['numerical_aperture', image.numerical_aperture],
    ['um_per_px', image.um_per_px ? `${Number(image.um_per_px).toPrecision(3)} µm/px` : null],
    ['camera', [image.camera_make, image.camera_model].filter(Boolean).join(' ') || null],
    ['exposure', image.exposure_s ? `${image.exposure_s} s` : null], ['iso', image.iso],
    ['size', image.width_px ? `${image.width_px} × ${image.height_px} px` : null],
    ['captured_at', image.captured_at],
  ].filter(([, v]) => v !== null && v !== undefined && v !== '');

  return (
    <section style={box} data-testid="microscopy-viewer">
      {/* Pinch, wheel, drag and the zoom buttons magnify the slide with its
          scale bar, which is drawn in the slide's own pixels. */}
      <ZoomableImage src={resolvePhotoUrl(image.url)} alt={t('imageAlt')} imageTestId="micro-img" inline
        keyboard={false} testId="micro-zoom" frameStyle={{ maxWidth: '100%', borderRadius: 6 }} drawing={measuring}
        imageStyle={{ maxWidth: '100%', maxHeight: '70vh' }}>
        {(overlay || measuring) && (
          <svg ref={svg} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%',
                     ...(measuring ? { cursor: 'crosshair', touchAction: 'none' } : { pointerEvents: 'none' }) }}
            data-testid="micro-overlay" data-measuring={measuring ? 'true' : 'false'}
            onPointerDown={measuring ? barDown : undefined} onPointerMove={measuring ? barMove : undefined}
            onPointerUp={measuring ? barUp : undefined} onPointerCancel={measuring ? () => setDrawn(null) : undefined}>
            <ScaleBarMark image={image} width={W} height={H} draft={measuring || line ? drawn || line : null} />
            {image.magnification && (
              <text x={12} y={Math.max(20, (image.height_px || 300) / 18)} fontSize={Math.max(12, (image.height_px || 300) / 25)}
                fill="#fff" stroke="#000" strokeWidth={0.6}>{image.objective || `${image.magnification}×`}</text>
            )}
          </svg>
        )}
      </ZoomableImage>
      <div style={{ margin: '8px 0' }}>
        <label><input type="checkbox" checked={overlay} onChange={() => setOverlay(!overlay)} title={t('tip.overlay')} /> {t('overlay')}</label>
        <span style={badge(image.scale_source === 'none' ? 'warning' : 'info')} data-testid="scale-source">
          {t(`scale_${image.scale_source}`)}</span>
      </div>
      <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 12px', fontSize: 13 }} data-testid="micro-meta">
        {meta.map(([k, v]) => (
          <div key={k} style={{ display: 'contents' }}><dt style={{ fontWeight: 600 }}>{t(`meta_${k}`)}</dt><dd style={{ margin: 0 }}>{v}</dd></div>
        ))}
      </dl>
      {image.exif_status === 'failed' && <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{image.exif_error}</p>}

      <h5>{t('links')}</h5>
      <ul data-testid="micro-links">{image.links.map((l) => (
        <li key={l.link_id}>{t(`link_${l.kind}`)} <code>{l.id.slice(0, 8)}</code>{l.version ? ` v${l.version}` : ''}{' '}
          {!readOnly && (
            <button type="button" data-testid={`unlink-${l.link_id}`} title={t('tip.unlink')}
              onClick={() => act(() => api.post(`/microscopy/${image.image_id}/links/${l.link_id}/remove`, { reason }))}>
              {t('unlink')}</button>
          )}</li>
      ))}</ul>
      {!readOnly && (
        <>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label style={label}>{t('linkByCode')}
              <input style={input} value={linkCode} onChange={(e) => setLinkCode(e.target.value)} title={t('tip.linkCode')} data-testid="link-code" />
            </label>
            <button type="button" disabled={!linkCode.trim()} onClick={linkByCode} title={t('tip.link')} data-testid="link-add">{t('link')}</button>
          </div>
          <ScaleBarForm key={image.image_id} image={image} line={line} measuring={measuring} onMeasure={setMeasuring}
            onSaved={(data) => { setImage(data); setLine(null); setMeasuring(false); onChange?.(data); }} />
          {['none', 'manual', 'scale_bar'].includes(image.scale_source) ? (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <label style={label}>{t('manualScale')}
                <input style={input} value={scale} onChange={(e) => setScale(e.target.value)} title={t('tip.manualScale')} data-testid="manual-scale" size={8} />
              </label>
              <button type="button" disabled={!(Number(scale) > 0)} data-testid="save-scale" title={t('tip.saveScale')}
                onClick={() => act(() => api.post(`/microscopy/${image.image_id}/scale`, { um_per_px: Number(scale), reason }))}>
                {t('saveScale')}</button>
            </div>
          ) : null}
          <label style={label}>{t('reason')}
            <input style={input} value={reason} onChange={(e) => setReason(e.target.value)} title={t('tip.reason')} data-testid="micro-reason" />
          </label>
        </>
      )}
      {error && <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{error}</p>}
      <ObservationPanel imageId={image.image_id} umPerPx={image.um_per_px} readOnly={readOnly} bundleId={image.bundle_id} />
    </section>
  );
}
